import type { BillingPlan, PurchasablePlan } from './types';

/**
 * Bucketing seats by plan.
 *
 * `GET /orgs/{id}/billing` now carries `plan` on every member row, read straight off the row. This
 * module used to infer the plan from the AI budget a seat held, because the field did not exist —
 * that inference is deleted, not kept as a fallback. It was wrong in a specific, damaging way: a seat
 * that bought Growth and later topped up held $130, matched no plan, and was labelled `Custom` even
 * though the backend could have said "Growth" plainly. There is no `custom` plan server-side, so the
 * case is not rare now — it is unreachable, and keeping a branch for it would be dead code that
 * quietly disagreed with the API.
 *
 * `plan` and `budget_usd` are independent facts from here on. A seat's plan says what was bought; its
 * budget says what it may spend, which top-ups and admin edits move around freely. Nothing should
 * ever try to derive one from the other again.
 */

const KNOWN_PLANS: readonly BillingPlan[] = ['free', 'starter', 'growth', 'scale'];

/**
 * Whether the wire value is a plan this build knows.
 *
 * NOT a return of the old inference — it reads no budget and guesses nothing. It exists because the
 * TypeScript type is a compile-time claim about a JSON payload, and the two can disagree for one
 * ordinary reason: the frontend and the backend deploy separately. Against an API that predates
 * `plan`, every row arrives with it `undefined`, and unguarded that renders a blank chip and silently
 * reports every plan as having nobody on it — wrong, and wrong without looking wrong.
 *
 * A value this build cannot name is reported as unknown rather than defaulted to `free`, because
 * calling a paid seat free is the one outcome worse than admitting we do not know.
 */
function isKnownPlan(value: unknown): value is BillingPlan {
  return typeof value === 'string' && (KNOWN_PLANS as readonly string[]).includes(value);
}

export interface SeatPlanTally {
  /** Seats on each purchasable plan. */
  byPlan: Record<PurchasablePlan, number>;
  /** Seats on the free plan. */
  free: number;
  /** Seats whose `plan` this build does not recognise. Zero against a matching backend. */
  unknown: number;
}

/**
 * Tally seats across the catalogue.
 *
 * Every seat lands in exactly one bucket, so `byPlan` totals plus `free` plus `unknown` always equals
 * the seat count. That property is what makes the counts safe to put on screen: a tally that dropped
 * a seat it could not classify would read as "nobody is on Growth" when the truth is "we cannot tell",
 * and the per-plan counts would silently disagree with the rows in the table below them.
 */
export function tallySeatPlans(seats: Array<{ plan: BillingPlan }>): SeatPlanTally {
  const tally: SeatPlanTally = {
    byPlan: { starter: 0, growth: 0, scale: 0 },
    free: 0,
    unknown: 0,
  };

  for (const seat of seats) {
    if (!isKnownPlan(seat.plan)) tally.unknown += 1;
    else if (seat.plan === 'free') tally.free += 1;
    else tally.byPlan[seat.plan] += 1;
  }

  return tally;
}

/**
 * Plans there is nobody left to buy for.
 *
 * A plan belongs to a SEAT, so "somebody is on Growth" must not take Growth off the table — another
 * member may still want it. A plan is only a dead end when EVERY active seat already holds it under a
 * live subscription, which is the one case the backend refuses with a 409. In a single-member
 * organization that condition is simply "this is my current plan".
 *
 * `liveSubscription` is passed in rather than derived here: whether a subscription counts as live is
 * the panel's `hasLiveSubscription` predicate, and a one-time seat is deliberately NOT live — buying
 * the same plan again is how a one-time seat tops up its budget, and the backend allows it.
 *
 * Takes the minimal shape it needs instead of `BillingSeat`, so this stays in the service layer
 * without importing a component module.
 */
export function plansAtCapacity(
  seats: ReadonlyArray<{ plan: BillingPlan; liveSubscription: boolean }>,
): PurchasablePlan[] {
  // No seats at all is not "every seat holds it" — `every` on an empty array is vacuously true, which
  // would mark all three plans as dead ends on an org with nobody to bill.
  if (seats.length === 0) return [];
  return (['starter', 'growth', 'scale'] as const).filter((plan) =>
    seats.every((seat) => seat.liveSubscription && seat.plan === plan),
  );
}

/** Display label for a purchasable plan. */
export const SEAT_PLAN_LABEL: Record<PurchasablePlan, string> = {
  starter: 'Starter',
  growth: 'Growth',
  scale: 'Scale',
};

/**
 * How a seat's plan reads in a chip.
 *
 * Returns `null` for a plan this build cannot name, so the caller renders an honest dash instead of
 * the empty string an unmatched `SEAT_PLAN_LABEL` lookup would produce.
 */
export function seatPlanLabel(plan: BillingPlan): string | null {
  if (!isKnownPlan(plan)) return null;
  return plan === 'free' ? 'Free' : SEAT_PLAN_LABEL[plan];
}
